import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { FaMapMarkerAlt, FaSave, FaTimes, FaUserPlus, FaUsers } from 'react-icons/fa';
import Swal from 'sweetalert2';
import AdminFormLayout from './AdminFormLayout';
import Navigation from './Navigation';

function MemberForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];

  const [outlets, setOutlets] = useState([]);

  const [formData, setFormData] = useState({
    outlet: '',
    first_name: '',
    last_name: '',
    hp: '',
    email: '',
    address: '',
    emergency_phone: '',
    birth_date: '',
    identity_no: '',
    status: 1,
    job: '',
    job_address: '',
    password: '',
    reff_id: ''
  });

  // ================= FETCH MEMBER (EDIT) =================
  const fetchMemberData = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}member/get`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
          },
          body: JSON.stringify({ id: parseInt(id) }),
        }
      );

      const data = await response.json();
      if (data.error === 0) {
        const member = data.data;
        setFormData({
          outlet: member.outlet.toString(),
          first_name: member.first_name,
          last_name: member.last_name,
          hp: member.hp,
          email: member.email,
          address: member.address,
          emergency_phone: member.emergency_phone || '',
          birth_date: member.birth_date?.split('T')[0] || '',
          identity_no: member.identity_no || '',
          status: member.status,
          job: member.job || '',
          job_address: member.job_address || '',
          password: '',
          reff_id: member.reff_id ? member.reff_id.toString() : '',
        });
      }
    } catch (error) {
      console.error('Error fetching member:', error);
    }
  }, [id]);

  // ================= FETCH OUTLETS =================
  const fetchOutlets = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}outletlist`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
          },
          body: JSON.stringify({ limit: 100, offset: 0 }),
        }
      );

      const data = await response.json();
      if (data.error === '0') {
        setOutlets(data.results);
      }
    } catch (error) {
      console.error('Error fetching outlets:', error);
    }
  };

  useEffect(() => {
    fetchOutlets();
    if (id) fetchMemberData();

    if (!id) {
      setFormData((prev) => ({
        ...prev,
        password: '12345678',
      }));
    }
  }, [id, fetchMemberData]);

  // ================= SUBMIT =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.birth_date) {
      Swal.fire({
        icon: 'warning',
        title: 'Birth date required',
        text: 'Tanggal lahir wajib diisi supaya dashboard bisa menampilkan ulang tahun member dengan benar.',
      });
      return;
    }

    const payload = {
      outlet: parseInt(formData.outlet),
      first_name: formData.first_name,
      last_name: formData.last_name,
      hp: formData.hp,
      email: formData.email,
      address: formData.address,
      emergency_phone: formData.emergency_phone,
      birth_date: formData.birth_date,
      identity_no: formData.identity_no,
      status: formData.status,
      job: formData.job,
      job_address: formData.job_address,
      reff_id: 0,
    };

    if (formData.password && formData.password.trim() !== '') {
      payload.password = formData.password;
    }

    if (id) payload.id = parseInt(id);

    const url = id
      ? `${process.env.REACT_APP_API_BASE_URL}member/update`
      : `${process.env.REACT_APP_API_BASE_URL}member/add`;

    try {
      const response = await fetch(url, {
        method: id ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.error === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: id ? 'Member updated successfully' : 'Member added successfully',
        }).then(() => navigate('/members'));
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to save member',
      });
    }
  };

  return (
    <div>
      <Navigation />
      <AdminFormLayout
        icon={id ? FaUsers : FaUserPlus}
        eyebrow="Client profile"
        title={id ? 'Edit Client' : 'Add New Client'}
        description="Simpan data client baru atau perbarui informasi kontak, akses, dan outlet aktif dengan tampilan yang lebih rapi."
        breadcrumbs={[
          { label: 'Dashboard', to: '/dashboardadmin' },
          { label: 'Clients', to: '/members' },
          { label: id ? 'Edit Client' : 'Add Client' },
        ]}
        tips={[
          'Email dan nomor HP dipakai untuk kontak client dan aktivitas follow-up.',
          'Tanggal lahir wajib akurat supaya widget ulang tahun di dashboard bisa dipakai admin.',
          'Kosongkan password saat edit jika tidak ingin mengganti akun login client.',
          'Pastikan outlet yang dipilih sesuai dengan cabang tempat client aktif.',
        ]}
      >
        <Form onSubmit={handleSubmit}>
          <section className="form-section">
            <div className="form-section__header">
              <span className="form-section__icon">
                <FaUsers />
              </span>
              <div className="form-section__copy">
                <h2 className="form-section__title">Client identity</h2>
                <p className="form-section__description">
                  Lengkapi identitas dan outlet client agar data registrasi tersimpan dengan benar.
                </p>
              </div>
            </div>

            <div className="form-grid">
              <Form.Group className="field-stack span-12">
                <Form.Label>Outlet</Form.Label>
                <Form.Select
                  value={formData.outlet}
                  onChange={(e) =>
                    setFormData({ ...formData, outlet: e.target.value })
                  }
                  required
                >
                  <option value="">Select Outlet</option>
                  {outlets.map((outlet) => (
                    <option key={outlet.id} value={outlet.id}>
                      {outlet.outlet_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <div className="span-12">
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="field-stack">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.first_name}
                        onChange={(e) =>
                          setFormData({ ...formData, first_name: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="field-stack">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.last_name}
                        onChange={(e) =>
                          setFormData({ ...formData, last_name: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <Form.Group className="field-stack span-6">
                <Form.Label>No HP</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.hp}
                  onChange={(e) =>
                    setFormData({ ...formData, hp: e.target.value })
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="field-stack span-6">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="field-stack span-6">
                <Form.Label>Tanggal Lahir</Form.Label>
                <Form.Control
                  type="date"
                  max={today}
                  value={formData.birth_date}
                  onChange={(e) =>
                    setFormData({ ...formData, birth_date: e.target.value })
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="field-stack span-6">
                <Form.Label>No Darurat</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.emergency_phone}
                  onChange={(e) =>
                    setFormData({ ...formData, emergency_phone: e.target.value })
                  }
                  placeholder="Nomor keluarga atau kontak darurat"
                />
              </Form.Group>

              <Form.Group className="field-stack span-6">
                <Form.Label>No Identitas</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.identity_no}
                  onChange={(e) =>
                    setFormData({ ...formData, identity_no: e.target.value })
                  }
                  placeholder="KTP / SIM / Passport"
                />
              </Form.Group>

              <Form.Group className="field-stack span-6">
                <Form.Label>Pekerjaan</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.job}
                  onChange={(e) =>
                    setFormData({ ...formData, job: e.target.value })
                  }
                  placeholder="Opsional"
                />
              </Form.Group>

              <Form.Group className="field-stack span-6">
                <Form.Label>Alamat Kerja</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.job_address}
                  onChange={(e) =>
                    setFormData({ ...formData, job_address: e.target.value })
                  }
                  placeholder="Opsional"
                />
              </Form.Group>

              <Form.Group className="field-stack span-12">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder={id ? 'Kosongkan jika tidak diganti' : ''}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
              </Form.Group>
            </div>
          </section>

          <section className="form-section">
            <div className="form-section__header">
              <span className="form-section__icon">
                <FaMapMarkerAlt />
              </span>
              <div className="form-section__copy">
                <h2 className="form-section__title">Address details</h2>
                <p className="form-section__description">
                  Simpan alamat lengkap member untuk kebutuhan administrasi dan pelayanan cabang.
                </p>
              </div>
            </div>

            <Form.Group className="field-stack">
              <Form.Label>Alamat</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                required
              />
            </Form.Group>
          </section>

          <div className="form-actions">
            <Button
              variant="secondary"
              onClick={() => navigate('/members')}
            >
              <FaTimes /> Cancel
            </Button>
            <Button type="submit" variant="primary">
              <FaSave /> {id ? 'Update' : 'Save'}
            </Button>
          </div>
        </Form>
      </AdminFormLayout>
    </div>
  );
}

export default MemberForm;
